/**
 * Printing / edition selector for a catalog-linked lot ("Price Variant").
 *
 * WHY THIS EXISTS
 * TCGplayer prices each PRINTING of a card separately — "Holofoil",
 * "Reverse Holofoil", "1st Edition Holofoil", "Unlimited Holofoil", plain
 * "1st Edition" vs "Unlimited", and so on. Those tiers are routinely 3-6x
 * apart (Fossil Raichu: 1st Ed $24.16 vs Unlimited $8.39), so picking the
 * wrong one is not a rounding error — it is the whole valuation.
 *
 * Before 2026-08-30 the app had no way to say which printing a lot actually
 * was: `cards.price_variant` / `sealed.price_variant` existed in Postgres and
 * both revalue_inventory() and the client honored them, but NOTHING ever
 * wrote them. Every lot fell through to a hardcoded name-ordered ladder that
 * happened to rank 1st Edition above Unlimited. This module is the missing
 * input.
 *
 * Semantics: empty value = "Auto", i.e. follow the default ladder (and keep
 * following it if TCGplayer adds tiers later). A non-empty value pins that
 * printing — revalue_inventory() gives it rank 0, ahead of the ladder.
 */
import { escapeHtml } from "../lib/dom";
import { fmt$ } from "../utils/format";
import { listCatalogVariants } from "../lib/catalogResolve";
import { multiplierFor } from "../lib/conditionMultiplier";

export const VARIANT_FIELD = "Price Variant";

/** Form-field markup. Renders closed//minimal: the current value is seeded as
 *  the selected option so the value round-trips even if the async populate
 *  never lands (offline, signed out, product unlinked). */
export function variantSelectHtml(current: string | null | undefined): string {
  const cur = String(current || "").trim();
  return `
    <div>
      <label class="label" for="f_Price_Variant">Printing / Edition</label>
      <select class="input" id="f_Price_Variant" name="${VARIANT_FIELD}">
        <option value="">Auto — best match</option>
        ${cur ? `<option value="${escapeHtml(cur)}" selected>${escapeHtml(cur)}</option>` : ""}
      </select>
      <div class="text-xs text-[var(--muted)] mt-1" id="variantHint">
        Which printing this lot is. 1st Edition and Unlimited are priced separately and often differ several-fold.
      </div>
    </div>`;
}

/**
 * Populate the selector from the catalog for `productId`, keeping `selected`
 * chosen. Safe to call repeatedly (after a Lookup pick, on modal open) and
 * safe to call with no product — it just degrades to the disabled "Auto" state.
 */
export async function syncVariantSelect(
  form: HTMLElement | null,
  productId: number,
  selected: string | null | undefined,
): Promise<void> {
  const sel = form?.querySelector<HTMLSelectElement>(`[name="${VARIANT_FIELD}"]`);
  if (!sel) return;
  const hint = form?.querySelector<HTMLElement>("#variantHint") || null;
  const want = String(selected ?? sel.value ?? "").trim();

  if (!(productId > 0)) {
    sel.innerHTML = `<option value="">Auto — best match</option>`
      + (want ? `<option value="${escapeHtml(want)}" selected>${escapeHtml(want)}</option>` : "");
    sel.disabled = true;
    if (hint) hint.textContent = "Link this lot to the catalog with ⌕ Lookup to choose a printing.";
    return;
  }

  const variants = await listCatalogVariants(productId);
  // The form may have moved on (different card picked) while we awaited.
  if (!sel.isConnected) return;

  if (!variants.length) {
    sel.innerHTML = `<option value="">Auto — best match</option>`
      + (want ? `<option value="${escapeHtml(want)}" selected>${escapeHtml(want)}</option>` : "");
    sel.disabled = true;
    if (hint) hint.textContent = "No catalog price for this product yet — nightly sync may not have reached it.";
    return;
  }

  const auto = variants[0]!;
  sel.disabled = false;
  sel.innerHTML = [
    `<option value="" ${want ? "" : "selected"}>Auto — ${escapeHtml(auto.variant)} (${fmt$(auto.unit)})</option>`,
    ...variants.map(v =>
      `<option value="${escapeHtml(v.variant)}" ${v.variant === want ? "selected" : ""}>${escapeHtml(v.variant)} — ${fmt$(v.unit)}</option>`),
    // A pinned variant that no longer has a price must not vanish silently.
    ...(want && !variants.some(v => v.variant === want)
      ? [`<option value="${escapeHtml(want)}" selected>${escapeHtml(want)} — no current price</option>`]
      : []),
  ].join("");

  if (hint) {
    hint.textContent = variants.length > 1
      ? `${variants.length} printings priced separately. Pick the one this lot actually is — nightly repricing will use it.`
      : "Only one printing is priced for this product; Auto is correct.";
  }
}

/** Wire the selector to update the FMV field when the user changes printing,
 *  so the number on screen always matches the tier that will be saved. */
export function wireVariantSelect(form: HTMLElement | null, fmvFieldName: string): void {
  const sel = form?.querySelector<HTMLSelectElement>(`[name="${VARIANT_FIELD}"]`);
  if (!sel) return;
  sel.addEventListener("change", () => {
    const opt = sel.selectedOptions[0];
    if (!opt) return;
    // Option labels carry the price ("Unlimited Holofoil — $2,146.38"); parse
    // rather than re-query, so switching printings is instant and offline-safe.
    const m = opt.textContent?.match(/\$([\d,]+(?:\.\d+)?)\s*\)?\s*$/);
    if (!m) return;
    const unit = Number(m[1]!.replace(/,/g, ""));
    if (!isFinite(unit)) return;
    // Option labels show the Near Mint catalog price; discount it for played
    // cards so the field matches what revalue_inventory() will write.
    const grade = form?.querySelector<HTMLSelectElement>('[name="Condition/Grade"]')?.value || "";
    const mult = multiplierFor(grade);
    const shown = +(unit * (mult ?? 1)).toFixed(2);
    const fmv = form?.querySelector<HTMLInputElement>(`[name="${fmvFieldName}"]`);
    if (fmv) { fmv.value = String(shown); fmv.dispatchEvent(new Event("input", { bubbles: true })); }
  });
}
